import type { ProgramWorkout } from '../types';

interface ActiveWorkoutState {
  workout: ProgramWorkout;
  startedAt: string;
  completedExercises: string[]; // IDs of completed exercises
  currentExerciseIndex: number;
  sets: Record<string, any[]>; // exercise_id -> sets array
  notes: Record<string, string>; // exercise_id -> notes
}

const STORAGE_KEY = 'active_workout_session';

/**
 * Service for managing active workout sessions
 * Persists workout state to localStorage so users can resume after navigation
 */
export class ActiveWorkoutService {
  /**
   * Save active workout state
   */
  saveActiveWorkout(state: ActiveWorkoutState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      console.log('Active workout saved:', state.workout.name);
    } catch (error) {
      console.error('Failed to save active workout:', error);
    }
  }

  /**
   * Get active workout state
   */
  getActiveWorkout(): ActiveWorkoutState | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const state = JSON.parse(stored) as ActiveWorkoutState;
      
      // Validate that the workout is not too old (e.g., more than 24 hours)
      const startedAt = new Date(state.startedAt);
      const now = new Date();
      const hoursSinceStart = (now.getTime() - startedAt.getTime()) / (1000 * 60 * 60);
      
      if (hoursSinceStart > 24) {
        console.log('Active workout expired (>24 hours), clearing...');
        this.clearActiveWorkout();
        return null;
      }

      console.log('Active workout loaded:', state.workout.name);
      return state;
    } catch (error) {
      console.error('Failed to load active workout:', error);
      return null;
    }
  }

  /**
   * Check if there's an active workout
   */
  hasActiveWorkout(): boolean {
    return this.getActiveWorkout() !== null;
  }

  /**
   * Clear active workout state
   */
  clearActiveWorkout(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      console.log('Active workout cleared');
    } catch (error) {
      console.error('Failed to clear active workout:', error);
    }
  }

  /**
   * Update active workout progress
   */
  updateProgress(updates: Partial<Omit<ActiveWorkoutState, 'workout' | 'startedAt'>>): void {
    const current = this.getActiveWorkout();
    if (!current) {
      console.warn('No active workout to update');
      return;
    }

    const updated: ActiveWorkoutState = {
      ...current,
      ...updates
    };

    this.saveActiveWorkout(updated);
  }

  /**
   * Add a set to an exercise
   */
  addSet(exerciseId: string, setData: any): void {
    const current = this.getActiveWorkout();
    if (!current) {
      console.warn('No active workout to add set to');
      return;
    }

    const exerciseSets = current.sets[exerciseId] || [];
    exerciseSets.push(setData);

    this.updateProgress({
      sets: {
        ...current.sets,
        [exerciseId]: exerciseSets
      }
    });
  }

  /**
   * Remove a set from an exercise
   */
  removeSet(exerciseId: string, setIndex: number): void {
    const current = this.getActiveWorkout();
    if (!current) {
      console.warn('No active workout to remove set from');
      return;
    }

    const exerciseSets = current.sets[exerciseId] || [];
    exerciseSets.splice(setIndex, 1);

    this.updateProgress({
      sets: {
        ...current.sets,
        [exerciseId]: exerciseSets
      }
    });
  }

  /**
   * Update notes for an exercise
   */
  updateNotes(exerciseId: string, notes: string): void {
    const current = this.getActiveWorkout();
    if (!current) {
      console.warn('No active workout to update notes for');
      return;
    }

    this.updateProgress({
      notes: {
        ...current.notes,
        [exerciseId]: notes
      }
    });
  }

  /**
   * Mark exercise as completed
   */
  completeExercise(exerciseId: string): void {
    const current = this.getActiveWorkout();
    if (!current) {
      console.warn('No active workout to complete exercise in');
      return;
    }

    if (!current.completedExercises.includes(exerciseId)) {
      this.updateProgress({
        completedExercises: [...current.completedExercises, exerciseId]
      });
    }
  }

  /**
   * Get workout duration in minutes
   */
  getDuration(): number {
    const current = this.getActiveWorkout();
    if (!current) {
      return 0;
    }

    const startedAt = new Date(current.startedAt);
    const now = new Date();
    const durationMs = now.getTime() - startedAt.getTime();
    return Math.floor(durationMs / (1000 * 60));
  }
}

// Export singleton instance
export const activeWorkoutService = new ActiveWorkoutService();
